import type {
  BoardPoint,
  BoardSize,
  GameHistoryItem,
  GameReviewFrame,
  StoneColor,
} from '@wx-weiqi/contracts';
import { researchStoneLabels } from './fox-kifu-presentation';

export type ReviewMarkerMode = 'LAST_ONE' | 'LAST_FIVE' | 'ALL';

/** Filter by variation move number, keeping captured moves and reused points truthful. */
export function reviewResearchStoneLabels(
  moves: readonly BoardPoint[],
  stones: readonly (StoneColor | null)[],
  size: BoardSize,
  mode: ReviewMarkerMode,
) {
  const labels = researchStoneLabels(moves, stones, size);
  if (mode === 'ALL') return labels;
  const firstVisible = moves.length - (mode === 'LAST_ONE' ? 1 : 5);
  return labels.filter(({ label }) => Number(label) > firstVisible);
}

export interface ReviewSideTimingSummary {
  readonly moveCount: number;
  readonly totalMs: number;
  readonly averageMs: number;
}

export interface ReviewTimingSummary {
  readonly currentMoveMs: number | null;
  readonly black: ReviewSideTimingSummary;
  readonly white: ReviewSideTimingSummary;
}

function localDateKey(timestamp: string): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function filterHistoryByDate(
  games: readonly GameHistoryItem[],
  date: string,
): readonly GameHistoryItem[] {
  if (date === '') return games;
  return games.filter((game) => localDateKey(game.finishedAt) === date);
}

function sideSummary(
  frames: readonly GameReviewFrame[],
  color: StoneColor,
): ReviewSideTimingSummary {
  const moves = frames.filter((frame) => frame.color === color && frame.elapsedMs !== null);
  const totalMs = moves.reduce((total, frame) => total + (frame.elapsedMs ?? 0), 0);
  return Object.freeze({
    moveCount: moves.length,
    totalMs,
    averageMs: moves.length === 0 ? 0 : totalMs / moves.length,
  });
}

export function summarizeReviewTiming(
  frames: readonly GameReviewFrame[],
  requestedMoveNumber: number,
): ReviewTimingSummary {
  const moveNumber = Math.max(0, Math.min(frames.length - 1, Math.trunc(requestedMoveNumber)));
  const visible = frames.slice(1, moveNumber + 1);
  return Object.freeze({
    currentMoveMs: frames[moveNumber]?.elapsedMs ?? null,
    black: sideSummary(visible, 'BLACK'),
    white: sideSummary(visible, 'WHITE'),
  });
}

export function reviewTimingRatio(left: number, right: number): readonly [number, number] {
  const maximum = Math.max(1, left, right);
  return Object.freeze([(left / maximum) * 100, (right / maximum) * 100]);
}

export function recentReviewTimingFrames(
  frames: readonly GameReviewFrame[],
  requestedMoveNumber: number,
  limit = 20,
): readonly GameReviewFrame[] {
  const moveNumber = Math.max(0, Math.min(frames.length - 1, Math.trunc(requestedMoveNumber)));
  const safeLimit = Math.max(0, Math.trunc(limit));
  if (safeLimit === 0) return Object.freeze([]);
  return Object.freeze(
    frames
      .slice(1, moveNumber + 1)
      .filter((frame) => frame.elapsedMs !== null)
      .slice(-safeLimit),
  );
}
